import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { fetchModalLoginUsersThunk, postModalLoginThunk } from '../../../redux/thunk/modalLogin'
import { setModalLoginInvalidPasswordError, setModalLoginLoading, setModalLoginUsers } from '../../../redux/action/modalLogin'
import IconsInput from '../inputs/IconsInput'
import { setSystemKeyboardText, setSystemShowKeyboard } from '../../../redux/action/system'
import CustomModal from '../modal/CustomModal'
import isRecaptchaEnabled from '../../../helper/isRecaptchaEnabled'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { ButtonsWrap, Form } from './index.styled'
import Loader from '../loaders'
import { IconItem } from '../inputs/IconsInput/index.styled'
import TextInput from '../inputs/TextInput'
import Button from '../buttons/Button'
import colors from '../../../style/colors'
import { theme } from '../../../style/theme'
import { useWindowBreakPoint } from '../../../hooks/useWindowBreakPoint'

const defaultValues = { password: '' }
const EVENT_Z_INDEX = 2

const ModalLogin = React.memo(({
																 isOpen, close, group, title, onLogin = () => {
	}, storeAccessToken = true, returnZIndex
															 }) => {
	const { t } = useTranslation()
	const users = useSelector(state => state.modalLogin.users)
	const invalidPasswordError = useSelector(store => store.modalLogin.invalidPasswordError)
	const companyIndex = useSelector(state => state.login.companyIndex)
	const keyboardEnterTrigger = useSelector(state => state.system.keyboardEnterTrigger)
	const showKeyboard = useSelector(state => state.system.showKeyboard)
	const { executeRecaptcha } = useGoogleReCaptcha()
	const recaptchaEnabled = isRecaptchaEnabled()
	const { handleSubmit, register, formState: { errors }, watch, setValue, reset } = useForm({ defaultValues })
	const loading = useSelector(store => store.modalLogin.loading)
	const isMobile = useWindowBreakPoint(theme.point720)
	const dispatch = useDispatch()

	useEffect(() => {
		dispatch(setModalLoginUsers(null))
		if (isOpen) {
			dispatch(fetchModalLoginUsersThunk(group))
		}
	}, [isOpen])// eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (keyboardEnterTrigger > 0 && showKeyboard) {
			handleSubmit(onSubmit)()
		}
	}, [keyboardEnterTrigger])// eslint-disable-line react-hooks/exhaustive-deps

	const handleClose = useCallback(() => {
		reset({
			...defaultValues
		})
		dispatch(setModalLoginInvalidPasswordError(false))
		close()
	}, [close, dispatch, reset])

	const onSubmit = useCallback(async ({ login, password }) => {
		dispatch(setModalLoginLoading(true))
		dispatch(setModalLoginInvalidPasswordError(false))
		let recaptchaValue = null
		if (recaptchaEnabled) {
			recaptchaValue = await executeRecaptcha()
		}
		dispatch(postModalLoginThunk(login, companyIndex, password, recaptchaValue, accessToken => {
			dispatch(setSystemKeyboardText(null))
			dispatch(setSystemShowKeyboard(false))
			onLogin(accessToken)
			handleClose()
		}, storeAccessToken))
	}, [recaptchaEnabled, executeRecaptcha, companyIndex, onLogin, storeAccessToken, dispatch, handleClose])

	const handleToBack = useCallback(() => {
		reset({
			...defaultValues
		})
	}, [reset])

	return (
		<>
			<CustomModal isOpen={isOpen}
									 title={title}
									 id="modal-login-form"
									 size="auto"
									 close={handleClose}
									 returnZIndex={returnZIndex}
									 disableBackButton={true}
									 showRecaptcha={true}
									 zIndex={EVENT_Z_INDEX}>
				<Form onSubmit={handleSubmit(onSubmit)}>
					{!users && <Loader color="black" />}
					{!watch('login') ?
						<IconsInput name="login"
												register={register}
												valuesList={users ? users.map(user => user.username) : []}
												setValue={setValue} />
						: <>
							<IconItem>
								<div className="icon-img icon-green">
									<svg viewBox="0 0 50 60" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M30.125 33.2609L37.75 39.1304C38.375 39.6522 38.5 40.5652 38 41.2174C37.75 41.6087 37.25 41.7391 36.875 41.7391C36.625 41.7391 36.25 41.6087 36 41.4783L28.375 35.6087C27.75 35.087 27.625 34.1739 28.125 33.5217C28.625 32.8696 29.5 32.7391 30.125 33.2609ZM11.125 12.7826C8.5 10.0435 7.5 6 8 1.30435C8.125 0.521739 8.75 0 9.375 0H10.625C11.125 0 11.5 0.260869 11.75 0.521739C12.125 1.04348 12.5 1.43478 13 1.82609C13.875 2.47826 14.75 3 15.75 3.26087C17.375 3.78261 20.375 3.65217 22.75 3.52174C23.625 3.52174 24.375 3.52174 25 3.52174C28.5 3.52174 31.375 6.39131 31.375 10.0435C31.375 11.8696 30.625 13.5652 29.375 14.7391V19.9565C29.375 20.087 29.375 20.087 29.375 20.2174L28.375 27.3913C27.875 29.8696 26.25 31.4348 24.125 31.4348H16.625C14.5 31.4348 12.875 29.8696 12.375 27.3913L11 20.2174C11 20.087 11 20.087 11 19.9565V12.7826H11.125ZM26.875 16.4348C26.375 16.5652 25.875 16.5652 25.375 16.5652C24.875 16.5652 24.375 16.5652 23.875 16.5652C20.5 16.5652 17 16.6957 14 15V19.8261L15.25 26.8696C15.25 27.1304 15.625 28.5652 16.625 28.5652H24.125C25.125 28.5652 25.5 27.1304 25.5 26.8696L26.75 19.8261L26.875 16.4348ZM13.5 10.9565C16.375 13.6957 20 13.6957 23.875 13.5652C24.375 13.5652 24.875 13.5652 25.375 13.5652C26.125 13.5652 26.75 13.3043 27.375 12.913L27.5 12.7826C28.375 12.1304 28.875 11.087 28.875 10.0435C28.875 8.08696 27.25 6.52174 25.375 6.52174C24.75 6.52174 24 6.52174 23.125 6.52174C20.875 6.52174 18.25 6.65217 16.25 6.3913C17.75 7.69565 19.375 8.21739 20.75 8.34783C21.5 8.47826 22.125 9.13044 22 9.91304C21.875 10.6957 21.25 11.3478 20.375 11.2174C17.875 10.9565 13.5 9.65217 11 3.91304C10.875 3.78261 10.75 3.78261 10.75 3.65217C10.75 6.65217 11.625 9.26087 13.5 10.9565ZM24 52.9565L21.875 50.8696V43.9565C21.875 43.1739 21.25 42.5217 20.375 42.5217C19.5 42.5217 18.875 43.1739 18.875 43.9565V50.8696L12.625 57.1304H9.375V47.8696C9.375 47.087 8.75 46.4348 7.875 46.4348C7.125 46.4348 6.375 47.087 6.375 47.8696V58.5652C6.375 59.3478 7 60 7.875 60H13.125C13.5 60 13.875 59.8696 14.125 59.6087L20.125 53.4783L21.625 55.0435C22.25 55.5652 23.125 55.5652 23.625 55.0435C24.5 54.3913 24.5 53.4783 24 52.9565ZM25.25 35.087C25 34.3044 24.125 34.0435 23.375 34.3043L20.375 35.6087L17.375 34.3043C16.625 34.0435 15.75 34.3044 15.5 35.087C15.125 35.8696 15.5 36.6522 16.25 37.0435L16.625 37.1739L16.25 37.3043C15.5 37.5652 15.125 38.4783 15.5 39.2609C15.875 40.0435 16.625 40.3044 17.375 40.0435L20.375 38.7391L23.375 40.0435C23.5 40.1739 23.75 40.1739 24 40.1739C24.625 40.1739 25.125 39.7826 25.375 39.2609C25.75 38.4783 25.375 37.6957 24.625 37.3043L24.25 37.1739L24.625 37.0435C25.25 36.6522 25.625 35.7391 25.25 35.087ZM50 58.5652C50 59.3478 49.375 60 48.5 60H45.75H28.125H25.375C24.625 60 23.875 59.3478 23.875 58.5652C23.875 57.7826 24.5 57.1304 25.375 57.1304H26.75V56.6087C26.75 51.3913 30.625 46.9565 35.625 46.3043V45.1304C35.625 44.3478 36.25 43.6957 37.125 43.6957C37.875 43.6957 38.625 44.3478 38.625 45.1304V46.3043C43.625 46.9565 47.5 51.3913 47.5 56.6087V57.1304H48.875C49.375 57.1304 50 57.7826 50 58.5652ZM44.25 56.4783C44.25 52.3043 40.875 48.913 36.875 48.913C32.75 48.913 29.5 52.3043 29.5 56.4783V57H44.25V56.4783ZM12.75 33.5217C12.25 32.8696 11.375 32.7391 10.75 33.2609L0.625 41.087C0.25 41.3478 0 41.7391 0 42.1304V58.4348C0 59.2174 0.625 59.8696 1.5 59.8696C2.375 59.8696 3 59.2174 3 58.4348V42.7826L12.5 35.3478C13.125 35.087 13.25 34.1739 12.75 33.5217Z" fill="white" />
									</svg>
								</div>
								<div className="icon-text black">{watch('login')}</div>
							</IconItem>
							<TextInput name="password"
												 label={'Passwort'}
												 type="text"
												 color="purple"
												 errors={!invalidPasswordError ? errors : { password: { message: t('Login.InvalidPasswordError') } }}
												 watch={watch('password')}
												 setValue={setValue}
												 keyBoard={!isMobile}
												 register={register}
												 options={{
													 required: t('Login.Required')
												 }}
												 passwordClass="password"
												 inputMode={isMobile ? 'text' : 'none'}/>
							<ButtonsWrap>
								<Button name="back" icon="back" text={t('buttons.controls.back')} keyButton="F4" onClick={handleToBack} testId="button-back" zIndex={EVENT_Z_INDEX} />
								<Button name="send" icon={!loading && 'checkmark'} color="green" type="submit" disabled={loading}>
									{loading ? <Loader color={colors.white} /> : t('Login.Submit')}
								</Button>
							</ButtonsWrap>
						</>}
				</Form>
			</CustomModal>
		</>
	)
})

ModalLogin.displayName = 'ModalLogin'
export default ModalLogin
